import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    DoCheck,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    isDevMode,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    Self,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroupDirective,
    NgControl,
    NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';

import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import {
    BehaviorSubject,
    merge,
    Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    UI_SELECT_CARD_PARENT_COMPONENT,
    UiSelectCardComponent,
} from './ui-select-card/ui-select-card.component';

@Component({
    selector: 'ui-select-card-group',
    templateUrl: './ui-select-card-group.component.html',
    styleUrls: ['./ui-select-card-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        NgForm,
        FormGroupDirective,
        {
            provide: MatFormFieldControl,
            useExisting: UiSelectCardGroupComponent,
        },
        {
            provide: UI_SELECT_CARD_PARENT_COMPONENT,
            useExisting: UiSelectCardGroupComponent,
        },
    ],
})
export class UiSelectCardGroupComponent implements OnInit,
    DoCheck,
    OnDestroy,
    AfterContentInit,
    MatFormFieldControl<any | any[]>,
    ControlValueAccessor {

    /**
     * Configure if the input should be marked as `required` inside the form field.
     *
     */
    @HostBinding('attr.aria-required')
    @Input()
    public get required() {
        return this._required;
    }

    /**
     * @ignore
     */
    public set required(required) {

        required = !!required || this._elementRef.nativeElement.hasAttribute('required');

        if (this._required !== required) {
            this._required = required;
            this.stateChanges.next();
        }
    }

    /**
     * Configure if the form control label should float.
     *
     */
    @HostBinding('class.floating')
    public get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    /**
     * Returns `true` if there are no items available.
     *
     */
    public get empty() {
        return !this._value || !this._value.length;
    }

    /**
     * Configure the component placeholder.
     *
     */
    @Input()
    public get placeholder() {
        return this._placeholder;
    }

    public set placeholder(placeholder) {
        this._placeholder = placeholder;
        this.stateChanges.next();
    }

    /**
     * Configure if the component is `readonly`.
     *
     */
    @HostBinding('class.readonly')
    @Input()
    public get readonly() {
        return this._readonly;
    }
    public set readonly(value) {
        this._readonly = !!value;
        this.stateChanges.next();
        this._cd.markForCheck();
    }

    /**
     * Configure if the component is `disabled`.
     *
     */
    @HostBinding('class.disabled')
    @Input()
    public get disabled() {
        return this._disabled;
    }
    public set disabled(value) {
        this._disabled = !!value;
        this.stateChanges.next();
        this._cd.markForCheck();
    }

    /**
     * Configure if the dropdown is in a `loading` state.
     *
     */
    @Input()
    public set loading(value: boolean) {
        this.loading$.next(value);
    }

    /**
     * Handles the dropdown value binding.
     *
     */
    @Input()
    public get value() {
        return this._value;
    }

    /**
     * @ignore
     */
    public set value(newValue: any) {
        const normalizedValue = this.multiple && !isArray(newValue) ?
            [newValue] :
            newValue;
        this._value = normalizedValue;
        this._updateSelection();
        this.stateChanges.next();
        this.registerChange(normalizedValue);
        this.selected.emit(normalizedValue);
    }

    @Input()
    public clearable = false;

    /**
     * Enable multiple selection
     *
     */
    @Input()
    public get multiple() { return this._multiple; }
    public set multiple(multiple: boolean) {
        if (multiple === this._multiple) { return; }
        this._multiple = multiple;

        if (multiple && !isArray(this._value)) {
            this._value = this._value ?
                [this._value] :
                [];
        }
    }

    @ContentChildren(UiSelectCardComponent, { descendants: true })
    public cards?: QueryList<UiSelectCardComponent>;

    /**
     * @ignore
     */
    public isDevMode = isDevMode();

    /**
     * @ignore
     */
    public errorState = false;

    /**
     * @ignore
     */
    public focused = false;

    /**
    * @ignore
    */
    @HostBinding()
    public id = `ui-select-card-group-${Math.random()}`;

    /**
     * @ignore
     */
    @HostBinding('attr.aria-describedby')
    public describedBy = '';

    /**
     * Emits the selected item value an item is selected.
     *
     */
    @Output()
    public selected = new EventEmitter<any | any[]>();

    /**
     * The component loading state source.
     *
     * @ignore
     */
    public loading$ = new BehaviorSubject(false);

    /**
     * @ignore
     */
    public inputControl = new FormControl('');

    /**
    * @ignore
    */
    public stateChanges = new Subject<void>();

    private _multiple = false;

    private _required = false;

    private _placeholder = '';
    private _readonly = false;
    private _disabled = false;
    private _destroyed$ = new Subject();
    private _value: any | any[];

    constructor(
        private _elementRef: ElementRef,
        private _errorStateMatcher: ErrorStateMatcher,
        private _parentForm: NgForm,
        private _parentFormGroup: FormGroupDirective,
        @Optional()
        @Self()
        public ngControl: NgControl,
        private _cd: ChangeDetectorRef,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    /**
     * @ignore
     */
    ngDoCheck() {
        if (!this.ngControl) { return; }

        const oldState = this.errorState;
        const control = this.ngControl ?
            this.ngControl.control as FormControl :
            null;
        const parent = this._parentFormGroup || this._parentForm;
        const newState = this._errorStateMatcher.isErrorState(control, parent);

        if (newState !== oldState) {
            this.errorState = newState;
            this.stateChanges.next();
        }
    }

    /**
     * Sets the select's value. Part of the ControlValueAccessor interface
     * required to integrate with Angular's core forms API.
     *
     * @param value New value to be written to the model.
     */
    public writeValue(value: any | any[]) {
        if (isEqual(value, this._value)) { return; }

        this.value = value;
        this._cd.markForCheck();
    }

    /**
     * Handles `click` events on the `form-control` container.
     *
     * @ignore
     */
    public onContainerClick(_: MouseEvent) { }

    /**
     * @ignore
     */
    public setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    /**
     * @ignore
     */
    ngOnInit() {
    }
    /**
     * @ignore
     */
    ngOnDestroy() {
        this._destroyed$.next();
    }
    /**
     * @ignore
     */
    ngAfterContentInit() {
        this._updateSelection();
        this.stateChanges.next();

        merge(
            ...this.cards!.map(card => card.toggleSelect$),
        ).pipe(
            takeUntil(this._destroyed$),
        ).subscribe(({ select, value }) => {
            if (select) {
                this.select(value);
            } else {
                this.deselect(value);
            }
        });
    }

    /**
     * @ignore
     */
    public registerChange = (_: any | any[]) => { };

    /**
     * @ignore
     */
    public registerTouch: (ev?: Event) => void = (_?: Event) => { };

    /**
     * @ignore
     */
    public registerOnChange(fn: (_: any | any[]) => {}) {
        this.registerChange = fn;
    }

    /**
     * @ignore
     */
    public registerOnTouched(fn: (ev?: Event) => void) {
        this.registerTouch = fn;
    }

    public select(newValue: any) {
        if (this._disabled || this._readonly) { return; }

        this.value = this.multiple ?
            [
                ...(
                    isArray(this.value) ?
                        this.value :
                        [this.value]
                ),
                newValue,
            ] :
            newValue;
    }

    public deselect(oldValue: any) {
        if (
            this._disabled ||
            this._readonly ||
            !this.multiple
        ) { return; }

        if (this.multiple) {
            this.value = (this.value as any[])
                .filter(option => option !== oldValue);
            return;
        }

        if (this.clearable && isEqual(oldValue, this.value)) {
            this.value = undefined;
        }
    }

    private _updateSelection() {
        if (!this.cards) { return; }
        this.cards
            .forEach(option => {
                option.selected = this.multiple ?
                    !!(this._value as any[] || []).find(selected => isEqual(selected, option.value)) :
                    isEqual(this._value, option.value);
            });
    }
}
